<script setup lang="ts">
import 'flag-icons/css/flag-icons.min.css';
import { computed, ref, useTemplateRef, watchEffect, type ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import i18n from '@/plugins/i18n';
import {
  getLanguageOptions,
  matchesLanguageQuery,
  parseAllowedLanguageCodesFromEnv,
  setCometLocale,
  type LanguageOption
} from '@/lib/cometLocale';

defineOptions({ name: 'LanguageSelector' });

const { t, locale } = useI18n();
const router = useRouter();

const allowed = parseAllowedLanguageCodesFromEnv();

const currentLocale = computed(() =>
  typeof locale.value === 'string' ? locale.value : String(locale.value)
);

const items = computed(() =>
  getLanguageOptions(
    allowed,
    code => {
      const key = `languageSelector.names.${code}`;
      const translated = t(key);
      return translated === key ? code : translated;
    },
    ['en', currentLocale.value]
  )
);

const currentOption = computed(() =>
  items.value.find(option => option.value === currentLocale.value)
);

const isFocused = ref(false);
const autocomplete = useTemplateRef<ComponentPublicInstance>('autocomplete');

// Vuetify drops the `placeholder` prop while the field has a value (always true here), so the
// search hint is set on the native input, only while focused to avoid trailing the selection.
watchEffect(
  () => {
    const input = autocomplete.value?.$el?.querySelector('input');
    if (!input) {
      return;
    }
    if (isFocused.value) {
      input.setAttribute('placeholder', t('languageSelector.searchPlaceholder'));
    } else {
      input.removeAttribute('placeholder');
    }
  },
  { flush: 'post' }
);

function filterLanguage(_title: string, query: string, item?: { raw: LanguageOption }) {
  return item ? matchesLanguageQuery(item.raw, query) : false;
}

async function onSelect(code: string | null) {
  if (!code) {
    return;
  }
  await setCometLocale(router, i18n, code);
}
</script>

<template>
  <v-autocomplete
    ref="autocomplete"
    class="comet-i18n-exclude comet-language-select"
    data-testid="comet-language-selector"
    density="compact"
    hide-details
    variant="outlined"
    rounded="lg"
    auto-select-first
    :aria-label="t('languageSelector.label')"
    :items="items"
    item-title="title"
    item-value="value"
    :custom-filter="filterLanguage"
    :no-data-text="t('languageSelector.noResults')"
    :menu-props="{ contentClass: 'comet-i18n-exclude', offset: 4 }"
    :model-value="currentLocale"
    @update:focused="isFocused = $event"
    @update:model-value="onSelect">
    <template #prepend-inner>
      <span
        v-if="currentOption?.flag"
        :class="['fi', `fi-${currentOption.flag}`, 'rounded-sm', 'flex-shrink-0']"
        aria-hidden="true" />
      <v-icon v-else icon="mdi-earth" />
    </template>

    <!-- A selection slot makes Vuetify clear the input on focus; otherwise typing appends to the
      selected title. Hiding it while focused keeps the input on one line. -->
    <template #selection="{ item }">
      <span v-if="!isFocused" class="text-truncate">{{ item.title }}</span>
    </template>

    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :data-testid="`comet-language-option-${item.raw.value}`">
        <template #prepend>
          <span
            v-if="item.raw.flag"
            :class="['fi', `fi-${item.raw.flag}`, 'rounded-sm', 'me-3']"
            aria-hidden="true" />
          <v-icon v-else icon="mdi-earth" class="me-3" />
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<style scoped>
.comet-language-select {
  max-width: 240px;
}
</style>
